/**
 * MusicManager.js - Music playback system for Memex Racing Game
 * 
 * Handles random music selection, playback, and auto-switching when songs end
 */

export class MusicManager {
  constructor(scene) {
    this.scene = scene;
    this.currentMusic = null;
    this.musicTracks = [];
    this.playedTracks = new Set();
    this.isPlaying = false;
    this.volume = 0.5;
    this.fadeTime = 1000; // 1 second fade transitions
    
    // Track list from /src/Music folder (actual files)
    this.trackList = [
      'czxcv',
      'fasdf',
      'm1',
      'm2',
      'mixkit-a-game-777',
      'mixkit-born-620',
      'mixkit-epic-games-76',
      'mixkit-epical-drums-01-676',
      'mixkit-epical-drums-02-677',
      'mixkit-epical-drums-03-678',
      'mixkit-epical-drums-04-679',
      'mixkit-feeling-happy-5',
      'mixkit-games-music-706',
      'mixkit-gimme-that-groove-872',
      'mixkit-tech-house-vibes-130',
      'xcvb',
      'zcv'
    ];
    
    console.log('[MusicManager] Initialized with', this.trackList.length, 'tracks');
  }
  
  /**
   * Load all music tracks
   */
  preloadMusic(scene) {
    console.log('[MusicManager] Preloading music tracks...');
    
    this.trackList.forEach(track => {
      const key = `music_${track}`;
      // Use path relative to public folder (assets/music/)
      const path = `assets/music/${track}.mp3`;
      
      try {
        scene.load.audio(key, [path]);
        this.musicTracks.push(key);
      } catch (error) {
        console.warn(`[MusicManager] Failed to load track: ${track}`, error);
      }
    });
    
    console.log('[MusicManager] Queued', this.musicTracks.length, 'tracks for loading');
  }
  
  /**
   * Start playing random music
   */
  startMusic() {
    console.log('[MusicManager] startMusic() called - isPlaying:', this.isPlaying);
    
    if (this.isPlaying) {
      console.log('[MusicManager] Music already playing');
      return;
    }
    
    // Check if we have loaded tracks
    console.log('[MusicManager] Available music tracks:', this.musicTracks.length);
    
    // Get a random track that hasn't been played yet
    const track = this.getRandomTrack();
    if (!track) {
      console.warn('[MusicManager] No tracks available to play');
      return;
    }
    
    console.log('[MusicManager] Selected track:', track);
    this.playTrack(track);
  }
  
  /**
   * Get a random unplayed track
   */
  getRandomTrack() {
    // Filter out already played tracks
    const availableTracks = this.musicTracks.filter(track => !this.playedTracks.has(track));
    
    // If all tracks have been played, reset the played list
    if (availableTracks.length === 0) {
      console.log('[MusicManager] All tracks played, resetting playlist');
      this.playedTracks.clear();
      return this.getRandomTrack();
    }
    
    // Select random track
    const randomIndex = Math.floor(Math.random() * availableTracks.length);
    return availableTracks[randomIndex];
  }
  
  /**
   * Play a specific track
   */
  playTrack(trackKey) {
    console.log(`[MusicManager] Attempting to play track: ${trackKey}`);
    
    try {
      // Stop current music if playing
      if (this.currentMusic) {
        this.fadeOut(() => {
          this.currentMusic.stop();
          this.currentMusic = null;
        });
      }
      
      // Check if audio exists
      if (!this.scene.cache.audio.exists(trackKey)) {
        console.warn(`[MusicManager] Track not loaded: ${trackKey}`);
        console.log('[MusicManager] Available audio cache keys:', Object.keys(this.scene.cache.audio.entries.entries));
        // Try next track
        setTimeout(() => this.startMusic(), 100);
        return;
      }
      
      console.log(`[MusicManager] Track ${trackKey} found in cache, creating sound...`);
      
      // Create and play new music
      this.currentMusic = this.scene.sound.add(trackKey, {
        volume: 0,
        loop: false
      });
      
      // Mark as played
      this.playedTracks.add(trackKey);
      
      // Start playing
      this.currentMusic.play();
      this.isPlaying = true;
      
      // Fade in
      this.fadeIn();
      
      // Set up end listener for auto-switching
      this.currentMusic.once('complete', () => {
        console.log(`[MusicManager] Track finished: ${trackKey}`);
        this.isPlaying = false;
        this.currentMusic = null;
        
        // Play next random track after a short pause
        setTimeout(() => this.startMusic(), 500);
      });
      
      console.log(`[MusicManager] Now playing: ${trackKey}`);
      
    } catch (error) {
      console.error('[MusicManager] Error playing track:', error);
      this.isPlaying = false;
      // Try another track
      setTimeout(() => this.startMusic(), 1000);
    }
  }
  
  /**
   * Fade in current music
   */
  fadeIn() {
    if (!this.currentMusic) return;
    
    this.scene.tweens.add({
      targets: this.currentMusic,
      volume: this.volume,
      duration: this.fadeTime,
      ease: 'Power2'
    });
  }
  
  /**
   * Fade out current music
   */
  fadeOut(callback) {
    if (!this.currentMusic) {
      if (callback) callback();
      return;
    }
    
    this.scene.tweens.add({
      targets: this.currentMusic,
      volume: 0,
      duration: this.fadeTime,
      ease: 'Power2',
      onComplete: callback
    });
  }
  
  /**
   * Stop all music
   */
  stopMusic() {
    if (this.currentMusic) {
      this.fadeOut(() => {
        if (this.currentMusic) {
          this.currentMusic.stop();
          this.currentMusic = null;
        }
        this.isPlaying = false;
      });
    }
  }
  
  /**
   * Pause music
   */
  pauseMusic() {
    if (this.currentMusic && this.isPlaying) {
      this.currentMusic.pause();
      this.isPlaying = false;
    }
  }
  
  /**
   * Resume music
   */
  resumeMusic() {
    if (this.currentMusic && !this.isPlaying) {
      this.currentMusic.resume();
      this.isPlaying = true;
    }
  }
  
  /**
   * Set music volume
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.currentMusic) {
      this.currentMusic.setVolume(this.volume);
    }
  }
  
  /**
   * Skip to next track
   */
  skipTrack() {
    console.log('[MusicManager] Skipping to next track');
    this.stopMusic();
    setTimeout(() => this.startMusic(), 100);
  }
  
  /**
   * Clean up
   */
  destroy() {
    this.stopMusic();
    this.musicTracks = [];
    this.playedTracks.clear();
  }
}

export default MusicManager;